'use client';

import { auth, db } from './firebase';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { collection, getDocs, limit, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';

export type AppUserRole = 'visitor' | 'officer' | 'admin';

export type UserRecord = {
  email: string;
  role?: AppUserRole | string;
  lastLogin?: any;
  counter?: any;
};

export async function signInAndGetRole(email: string, password: string): Promise<{ role: AppUserRole; email: string }>{
  const lowerEmail = email.trim().toLowerCase();

  const cred = await signInWithEmailAndPassword(auth, lowerEmail, password);
  const authedEmail = cred.user.email?.toLowerCase() || lowerEmail;

  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('email', '==', authedEmail), limit(1));
  const snap = await getDocs(q);

  if (snap.empty) {
    await signOut(auth);
    throw new Error('No user record found for this email. Please contact your administrator.');
  }

  const docSnap = snap.docs[0];
  const data = docSnap.data() as UserRecord;
  const role = (data.role as AppUserRole | undefined)?.toString() as AppUserRole | undefined;

  if (!role || !['visitor', 'officer', 'admin'].includes(role)) {
    await signOut(auth);
    throw new Error('Your account does not have a valid role assigned.');
  }

  // Best-effort lastLogin update; do not block on failure
  try {
    await updateDoc(docSnap.ref, { lastLogin: serverTimestamp() });
  } catch (_) {}

  return { role, email: authedEmail };
}

export async function getRoleByEmail(email: string): Promise<{ role: AppUserRole; email: string } | null> {
  const lowerEmail = email.trim().toLowerCase();
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('email', '==', lowerEmail), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const data = snap.docs[0].data() as UserRecord;
  const role = (data.role as AppUserRole | undefined)?.toString() as AppUserRole | undefined;
  if (!role || !['visitor', 'officer', 'admin'].includes(role)) return null;
  return { role, email: lowerEmail };
}

export async function getCurrentUserRole(): Promise<{ role: AppUserRole; email: string } | null> {
  const user = auth.currentUser;
  const email = user?.email?.toLowerCase();
  if (!email) return null;
  return await getRoleByEmail(email);
}

export function roleToRedirectPath(role: AppUserRole, email: string): string {
  if (role === 'admin') return `/admin/dashboard?${new URLSearchParams({ officerId: email }).toString()}`;
  if (role === 'officer') return `/officer/dash?${new URLSearchParams({ officerId: email }).toString()}`;
  return '/visitor';
}

